/**
 * AppWorkspaceShell — khung Deep Workspace CHÍNH cho user đã đăng nhập.
 *
 *   ┌──────────┬───────────────────────┬────────────┐
 *   │ Sidebar  │ Nội dung route         │  AI Panel  │
 *   │ (credits │                        │            │
 *   │  bell    │                        │            │
 *   │  AI btn) │                        │            │
 *   └──────────┴───────────────────────┴────────────┘
 *
 * Không có header riêng — credits, thông báo và nút Trợ lý AI nằm trong
 * footer sidebar. Mobile: hamburger FAB cố định góc trái.
 */

import React, { useEffect } from 'react';
import { Menu, Minimize2 } from 'lucide-react';
import { WorkspaceProvider, useWorkspace } from '../context/WorkspaceProvider';
import { WorkspaceSidebar, type SidebarUserProfile } from './WorkspaceSidebar';
import { AIAssistantPanel } from './AIAssistantPanel';
import type { WorkspaceMode } from '../types';

interface AppWorkspaceShellProps {
  mode: WorkspaceMode;
  userName?: string;
  onNavigate: (path: string, payload?: any) => void;
  onShowToast?: (message: string, type?: 'success' | 'error' | 'info') => void;

  user: any;
  notifications: any[];
  onLogout: () => void;
  onMarkAsRead?: (id: string) => void;
  onMarkAllAsRead?: () => void;
  onClearRead?: () => void;
  onSwitchRole?: (role: 'teacher' | 'parent') => void;
  activeRoleConfig: any;

  sidebarFooter?: React.ReactNode;
  children: React.ReactNode;
}

const ShellInner: React.FC<AppWorkspaceShellProps> = ({
  mode,
  onNavigate,
  user,
  notifications,
  onLogout,
  onMarkAsRead,
  onMarkAllAsRead,
  onClearRead,
  onSwitchRole,
  activeRoleConfig,
  sidebarFooter,
  children,
}) => {
  const {
    isSidebarCollapsed,
    toggleSidebarCollapse,
    isMobileSidebarOpen,
    setMobileSidebarOpen,
    isAIPanelOpen,
    toggleAIPanel,
    focusMode,
    toggleFocusMode,
    fillContent,
    parentChildren,
    activeChildId,
    setActiveChildId,
  } = useWorkspace();

  const children_list = mode === 'parent' ? parentChildren : [];

  // Auto-select first child on mount if none stored / stored id no longer valid
  useEffect(() => {
    if (mode !== 'parent' || children_list.length === 0) return;
    const valid = children_list.some(c => String(c.id) === activeChildId);
    if (!valid) setActiveChildId(String(children_list[0].id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, children_list.length]);

  // Focused Mode: chỉ còn nội dung + nút thoát.
  if (focusMode) {
    return (
      <div className="h-screen w-full overflow-hidden bg-[#f8fafc] relative">
        <div className="h-full w-full overflow-hidden">{children}</div>
        <button
          onClick={toggleFocusMode}
          className="fixed top-4 right-4 z-50 flex items-center gap-1.5 bg-slate-900/90 hover:bg-slate-900 text-white font-semibold px-3 py-2 rounded-xl text-xs shadow-lg backdrop-blur"
          title="Thoát chế độ tập trung (Esc)"
        >
          <Minimize2 className="w-4 h-4" /> Thoát tập trung
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden bg-[#f8fafc] text-slate-800">
      {/* Mobile hamburger FAB — thay thế header button (desktop không cần) */}
      <button
        onClick={() => setMobileSidebarOpen(true)}
        className="md:hidden fixed top-3 left-3 z-30 w-9 h-9 bg-white border border-slate-200 rounded-xl shadow-md flex items-center justify-center text-slate-500 hover:text-slate-700"
        title="Mở menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* ── Child selector bar (Parent workspace only) ──────────────────── */}
      {mode === 'parent' && children_list.length > 0 && (
        <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-white border-b border-slate-100 overflow-x-auto">
          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide flex-shrink-0">Đang theo dõi:</span>
          {children_list.map(child => {
            const isActive = String(child.id) === activeChildId;
            return (
              <button
                key={child.id}
                onClick={() => setActiveChildId(String(child.id))}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all border ${
                  isActive
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-extrabold flex-shrink-0 ${isActive ? 'bg-indigo-500' : 'bg-indigo-100 text-indigo-600'}`}>
                  {child.name.charAt(0).toUpperCase()}
                </span>
                {child.name}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        <WorkspaceSidebar
          mode={mode}
          collapsed={isSidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapse}
          mobileOpen={isMobileSidebarOpen}
          onCloseMobile={() => setMobileSidebarOpen(false)}
          onNavigate={(path) => onNavigate(path)}
          footerSlot={sidebarFooter}
          onNavigateView={onNavigate}
          onLogout={onLogout}
          onSwitchRole={onSwitchRole}
          activeRoleConfig={activeRoleConfig}
          isStudentCode={user?.authType === 'student_code'}
          isAffiliateActive={!!user?.isAffiliateActive}
          notifications={notifications}
          onMarkAsRead={onMarkAsRead}
          onMarkAllAsRead={onMarkAllAsRead}
          onClearRead={onClearRead}
          onToggleAIPanel={toggleAIPanel}
          isAIPanelOpen={isAIPanelOpen}
          userProfile={user ? {
            name: user.teacherName || user.name || 'Người dùng',
            credits: user.totalCredits,
            tier: user.aiTier,
            avatarUrl: user.avatar,
            lowCredits: typeof user.totalCredits === 'number' && user.totalCredits < 500,
          } as SidebarUserProfile : undefined}
        />

        <main className={`flex-1 min-w-0 min-h-0 ${fillContent ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          {children}
        </main>

        <AIAssistantPanel />
      </div>
    </div>
  );
};

export const AppWorkspaceShell: React.FC<AppWorkspaceShellProps> = (props) => (
  <WorkspaceProvider mode={props.mode} userName={props.userName} onShowToast={props.onShowToast}>
    <ShellInner {...props} />
  </WorkspaceProvider>
);
